import { history } from 'instantsearch.js/es/lib/routers'
import type { UiState } from 'instantsearch.js'

export function createCategoryURL({
  qsModule,
  routeState,
  location,
}: {
  qsModule: any
  routeState: any
  location: Location
}): string {
  if (!location.pathname.includes('category') && !location.pathname.includes('search')) {
    return location.href
  }

  const pathSegments = location.pathname.split('/').filter(Boolean)
  const countrySegment = pathSegments[0] || ''
  const languageSegment = pathSegments[1] || ''

  const includeCountry = countrySegment && (countrySegment.length === 2 || countrySegment.length === 3)
  const includeLanguage = languageSegment && (languageSegment.length === 2 || languageSegment.length === 3)

  const baseUrl = `${location.origin}/`
  let path = ''

  if (includeCountry) {
    path = `${countrySegment}`
    if (includeLanguage) {
      path += `/${languageSegment}`
    }
    path += '/'
  }

  if (routeState.slug) {
    path += `category/${routeState.slug}`
  } else {
    path += 'search'
  }

  const queryParameters = {} as { query?: string; page?: number; supplier?: string }
  if (routeState.query) queryParameters.query = encodeURIComponent(routeState.query)
  if (routeState.page && routeState.page !== 1) queryParameters.page = routeState.page
  if (routeState.supplier) queryParameters['menu[supplier]'] = routeState.supplier

  if (routeState.refinementList) {
    Object.keys(routeState.refinementList).forEach((key) => {
      routeState.refinementList[key].forEach((value: string, index: string) => {
        const encodedKey = `refinementList[${key}][${index}]`
        queryParameters[encodedKey] = value
      })
    })
  }

  if (routeState.toggle) {
    Object.keys(routeState.toggle).forEach((key) => {
      queryParameters[`toggle[${key}]`] = 'true'
    })
  }

  const queryString = qsModule.stringify(queryParameters, {
    addQueryPrefix: true,
    arrayFormat: 'repeat',
  })
  return `${baseUrl}${path}${queryString}`
}

export function parseCategoryURL({ qsModule, location }: { qsModule: any; location: Location }) {
  const pathSegments = location.pathname.split('/')
  const categoryIndex = pathSegments.indexOf('category')
  const slug = categoryIndex !== -1 && pathSegments[categoryIndex + 1] ? pathSegments[categoryIndex + 1] : undefined

  const queryParams = qsModule.parse(location.search.slice(1), { depth: 100 })

  const query = queryParams.query ? safeDecodeURIComponent(queryParams.query) : ''
  const { page } = queryParams
  const supplier = queryParams.menu ? queryParams.menu.supplier : undefined

  const refinementList = queryParams.refinementList || {}
  const toggle = queryParams.toggle ? { ...queryParams.toggle } : {}

  return {
    query,
    page,
    slug,
    supplier,
    refinementList,
    toggle,
  }
}

export function createSearchURL({
  qsModule,
  routeState,
  location,
  getSlugFromCategories,
}: {
  qsModule: any
  routeState: any
  location: Location
  getSlugFromCategories: (categories: string[]) => string
}): string {
  if (!location.pathname.includes('search') && !location.pathname.includes('category')) {
    return location.href
  }

  const pathSegments = location.pathname.split('/').filter(Boolean)
  const countrySegment = pathSegments[0] || ''
  const languageSegment = pathSegments[1] || ''

  const includeCountry = countrySegment && (countrySegment.length === 2 || countrySegment.length === 3)
  const includeLanguage = languageSegment && (languageSegment.length === 2 || languageSegment.length === 3)

  const baseUrl = `${location.origin}/`
  let path = ''

  if (includeCountry) {
    path = `${countrySegment}`
    if (includeLanguage) {
      path += `/${languageSegment}`
    }
    path += '/'
  }

  const queryParameters = {} as { query?: string; page?: number; supplier?: string }
  if (routeState.query) queryParameters.query = encodeURIComponent(routeState.query)
  if (routeState.page && routeState.page !== 1) queryParameters.page = routeState.page
  if (routeState.supplier) queryParameters['menu[supplier]'] = routeState.supplier

  if (routeState.hierarchicalMenu) {
    const slug = getSlugFromCategories(routeState.hierarchicalMenu)
    if (slug) {
      path += `category/${slug}`
    } else {
      path += 'search'
    }
  } else {
    path += 'search'
  }

  if (routeState.refinementList) {
    Object.keys(routeState.refinementList).forEach((key) => {
      routeState.refinementList[key].forEach((value: string, index: string) => {
        const encodedKey = `refinementList[${key}][${index}]`
        queryParameters[encodedKey] = value
      })
    })
  }

  if (routeState.toggle) {
    Object.keys(routeState.toggle).forEach((key) => {
      queryParameters[`toggle[${key}]`] = 'true'
    })
  }

  const queryString = qsModule.stringify(queryParameters, {
    addQueryPrefix: true,
    arrayFormat: 'repeat',
  })
  return `${baseUrl}${path}${queryString}`
}

export function parseSearchURL({ qsModule, location }: { qsModule: any; location: Location }) {
  const queryParams = qsModule.parse(location.search.slice(1), { depth: 100 })
  const query = queryParams.query ? safeDecodeURIComponent(queryParams.query) : ''
  const { page } = queryParams
  const supplier = queryParams.menu ? queryParams.menu.supplier : undefined

  const refinementList = queryParams.refinementList || {}
  const hierarchicalMenu = queryParams.hierarchicalMenu || {}
  const toggle = queryParams.toggle ? { ...queryParams.toggle } : {}

  return {
    query,
    page,
    hierarchicalMenu,
    supplier,
    refinementList,
    toggle,
  }
}

export function createCategoryRouting(
  algoliaIndex: string,
  getCategoriesArrayFromSlug: (slug: string) => string[],
  getSlugFromCategories: (categories: string[]) => string,
) {
  return {
    router: history({
      cleanUrlOnDispose: false,
      createURL: createCategoryURL,
      parseURL: parseCategoryURL,
      push(url: string) {
        if (typeof window !== 'undefined') {
          window.history.pushState(null, '', url)
          window.dispatchEvent(new PopStateEvent('popstate', { state: null }))
        }
      },
    }),

    stateMapping: {
      stateToRoute(uiState: UiState) {
        const indexUiState = uiState[algoliaIndex] || {}
        return {
          query: indexUiState.query,
          page: indexUiState.page,
          slug: indexUiState.hierarchicalMenu && getSlugFromCategories(indexUiState.hierarchicalMenu.categoriesLevel0),
          supplier: indexUiState.menu && indexUiState.menu.supplier,
          refinementList: indexUiState.refinementList,
          toggle: indexUiState.toggle ? { ...indexUiState.toggle } : {},
        }
      },

      routeToState(routeState: Record<string, any>) {
        return {
          [algoliaIndex]: {
            query: routeState.query,
            page: routeState.page,
            hierarchicalMenu: {
              categoriesLevel0: routeState.slug ? getCategoriesArrayFromSlug(routeState.slug).filter(Boolean) : [],
            },
            menu: {
              supplier: routeState.supplier,
            },
            refinementList: routeState.refinementList || {},
            toggle: routeState.toggle ? { ...routeState.toggle } : {},
          },
        }
      },
    },
  }
}

export function createSearchRouting(algoliaIndex: string, getSlugFromCategories: (categories: string[]) => string) {
  return {
    router: history({
      cleanUrlOnDispose: false,
      createURL: (params) => createSearchURL({ ...params, getSlugFromCategories }),
      parseURL: parseSearchURL,
      push(url: string) {
        if (typeof window !== 'undefined') {
          window.history.pushState(null, '', url)
          window.dispatchEvent(new PopStateEvent('popstate', { state: null }))
        }
      },
    }),

    stateMapping: {
      stateToRoute(uiState: UiState) {
        const indexUiState = uiState[algoliaIndex] || {}
        return {
          query: indexUiState.query,
          page: indexUiState.page,
          hierarchicalMenu: indexUiState.hierarchicalMenu && indexUiState.hierarchicalMenu.categoriesLevel0,
          supplier: indexUiState.menu && indexUiState.menu.supplier,
          refinementList: indexUiState.refinementList,
          toggle: indexUiState.toggle ? { ...indexUiState.toggle } : {},
        }
      },

      routeToState(routeState: Record<string, any>) {
        return {
          [algoliaIndex]: {
            query: routeState.query,
            page: routeState.page,
            hierarchicalMenu: routeState.hierarchicalMenu
              ? {
                  categoriesLevel0: routeState.hierarchicalMenu.categoriesLevel0,
                }
              : {},
            menu: {
              supplier: routeState.supplier,
            },
            refinementList: routeState.refinementList || {},
            toggle: routeState.toggle ? { ...routeState.toggle } : {},
          },
        }
      },
    },
  }
}

const safeDecodeURIComponent = (str: string) => {
  try {
    return decodeURIComponent(str)
  } catch (e) {
    if (e instanceof URIError) {
      return str
    }
    throw e
  }
}
